import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAngemeldet } from "../sitzung";
import { pushEinschalten, pushZustand } from "../push";
import { fehlertext } from "../fehler";
import { ersteSchritteAus, ersteSchritteAusblenden } from "../speicher";
import { FotoKnopf, useFoto } from "./FotoKnopf";
import { Knopf, KnopfLink } from "./Knopf";
import { useHinweis } from "./Hinweise";
import s from "./ui.module.css";

/** Wie viele der drei Schritte erledigt sind. Alles erledigt: Die Karte verschwindet von selbst. */
export function ersteSchritteStand(stand: { foto: boolean; push: boolean; aufgabe: boolean }): { erledigt: number; fertig: boolean } {
  const erledigt = [stand.foto, stand.push, stand.aufgabe].filter(Boolean).length;
  return { erledigt, fertig: erledigt === 3 };
}

/** Karte „Erste Schritte“ auf Meine Woche (BAUPLAN-PAKETE.md, Abschnitt 4.6): Foto hochladen, Push auf diesem Gerät einschalten,
 *  eine offene Aufgabe übernehmen. `aufgabe`: Die Person ist für mindestens eine Aufgabe verantwortlich. Keine eigene Tabelle:
 *  „Ausblenden“ merkt sich das Gerät. */
export function ErsteSchritte({ aufgabe }: { aufgabe: boolean }) {
  const ich = useAngemeldet().person.id;
  const foto = useFoto();
  const { zeigen } = useHinweis();
  const qc = useQueryClient();
  const [aus, setAus] = useState(() => ersteSchritteAus(ich));
  const [pushLaeuft, setPushLaeuft] = useState(false);
  const push = useQuery({ queryKey: ["push", "zustand"], queryFn: pushZustand, staleTime: 0 });
  const pushAn = push.data?.zustand === "an";
  const stand = ersteSchritteStand({ foto: foto.hatFoto, push: pushAn, aufgabe });

  if (aus || stand.fertig || push.isPending) return null;

  async function pushAnschalten() {
    setPushLaeuft(true);
    try {
      await pushEinschalten();
      zeigen("Push ist auf diesem Gerät an.");
    } catch (e) {
      zeigen(fehlertext(e), { fehler: true });
    } finally {
      setPushLaeuft(false);
      void qc.invalidateQueries({ queryKey: ["push"] });
    }
  }

  const schritte = [
    {
      titel: "Foto hochladen",
      text: "Damit dich die anderen in Listen und auf dem Board erkennen.",
      fertig: foto.hatFoto,
      knopf: <FotoKnopf foto={foto} klein />,
    },
    {
      titel: "Push einschalten",
      text: "Du erfährst sofort, wenn dir jemand eine Aufgabe gibt oder dich erwähnt.",
      fertig: pushAn,
      // Einschalten geht nur direkt aus einem Klick. Auf dem iPhone ohne installierte App oder bei abgelehnter Erlaubnis: Anleitung im Profil.
      knopf:
        push.data?.zustand === "aus" ? (
          <Knopf klein onClick={() => void pushAnschalten()} disabled={pushLaeuft}>
            {pushLaeuft ? "Einen Moment …" : "Push einschalten"}
          </Knopf>
        ) : (
          <KnopfLink klein to="/profil#push">
            So geht es
          </KnopfLink>
        ),
    },
    {
      titel: "Eine offene Aufgabe übernehmen",
      text: "Unter „Offen“ steht, wofür noch jemand gesucht wird.",
      fertig: aufgabe,
      knopf: (
        <KnopfLink klein to="/offen">
          Zu Offen
        </KnopfLink>
      ),
    },
  ];

  return (
    <section className={[s.karte, s.ersteSchritte].join(" ")} aria-labelledby="erste-schritte-titel">
      <div className={s.ersteKopf}>
        <div>
          <h2 id="erste-schritte-titel">Erste Schritte</h2>
          <p className={s.unterzeile}>{stand.erledigt} von 3 erledigt</p>
        </div>
        <Knopf
          art="leise"
          klein
          onClick={() => {
            ersteSchritteAusblenden(ich);
            setAus(true);
          }}
        >
          Ausblenden
        </Knopf>
      </div>
      <ol className={s.ersteListe}>
        {schritte.map((sch, i) => (
          <li key={sch.titel} className={[s.innen, s.ersterSchritt].join(" ")} data-fertig={sch.fertig}>
            <span className={s.ersteZahl} aria-hidden="true">
              {sch.fertig ? (
                <svg viewBox="0 0 24 24" width="14" height="14">
                  <path d="M6 12.5l4 4 8-9" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : (
                i + 1
              )}
            </span>
            <div>
              <b>
                {sch.titel}
                {sch.fertig ? <span className="nur-leser"> (erledigt)</span> : null}
              </b>
              <p>{sch.text}</p>
              {sch.fertig ? <p className={s.ersteErledigt}>Erledigt</p> : sch.knopf}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
